import { useEffect, useState } from 'react'
import { Button, Col, Pagination, Row, Spinner, Table } from 'react-bootstrap'
import { humanizeDateAndSetArgTimezone } from '../../../../Utils/Dates/Dates'
import { getRequests } from '../../Api/Requests/Requests'
import { Request } from '../../Api/Requests/models'
import { Title } from '../../../../Templates/Title'
import { BsFillPersonLinesFill } from 'react-icons/bs'
import { useSocket } from '../../../../hooks/useSocket'
import { useAdminInfo } from '../../../../hooks/useAdminInfo'
import { useRequestTable } from './useRequestTable'
import { OpenRequestModal } from './components/OpenRequestModal'
import Swal from 'sweetalert2'
import { ShowPlayerDataModal } from './components/ShowPlayerDataModal'
import { FaEye } from 'react-icons/fa'
import { MdOpenInNew } from 'react-icons/md'
import { OpenSubtractRequest } from './components/OpenSubtractRequest'
import { OpenPaymentRequest } from './components/OpenPaymentRequest'
import { PiHandCoinsFill } from 'react-icons/pi'

interface Filters {
  status: string
  platformUser: string
}

export const Requests = () => {
  // Hooks
  const { adminInfo } = useAdminInfo();
  const { socket } = useSocket();
  const {
    getStatusLabel,
    getStatusColor,
    getTypeLabel,
    getTypeColor,
  } = useRequestTable();

  const [loading, setLoading] = useState(false)
  const [requests, setRequests] = useState<Request[]>([])
  const [filters, setFilters] = useState<Filters>({ status: 'pending', platformUser: '' })

  // Pagination
  const [totalCount, setTotalCount] = useState(0)
  const [totalPages, setTotalPages] = useState(0)
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(30)


  // Modals
  const [showOpenRequestModal, setShowOpenRequestModal] = useState(false)
  const [selectedRequest, setSelectedRequest] = useState<Request | null>(null)

  const [showOpenSubtractRequestModal, setShowOpenSubtractRequestModal] = useState(false)
  const [selectedSubtractRequest, setSelectedSubtractRequest] = useState<Request | null>(null)

  const [showPlayerDataModal, setShowPlayerDataModal] = useState(false)
  const [showPlayerDataRequest, setShowPlayerDataRequest] = useState<Request | null>(null)

  const [showOpenPaymentRequestModal, setShowOpenPaymentRequestModal] = useState(false)
  const [selectedPaymentRequest, setSelectedPaymentRequest] = useState<Request | null>(null)


  const fetchRequests = async ({ page = 1 }: { page: number }) => {
    const response = await getRequests({
      page: page,
      status: filters.status,
      platformUser: filters.platformUser
    })
    if (response?.success) {
      setRequests(response?.requests)
      setTotalCount(response?.totalCount)
      setTotalPages(Math.ceil(response?.totalCount / pageSize))
      setCurrentPage(response?.page)
      setLoading(false)
    }
  }

  const handlePageChange = (page: number) => {
    setCurrentPage(page)
    setLoading(true)
    fetchRequests({ page: page })
  }

  const handleOpenRequest = (request: Request) => {
    if (request.type === 'load') {
      setSelectedRequest(request)
      setShowOpenRequestModal(true)
    } else if (request.type === 'withdraw') {

      if (request.status === 'pendingpayment') {
        setSelectedPaymentRequest(request)
        setShowOpenPaymentRequestModal(true)
      } else {
        setSelectedSubtractRequest(request)
        setShowOpenSubtractRequestModal(true)
      }

    }

  }

  const handleShowPlayerData = (request: Request) => {
    setShowPlayerDataRequest(request)
    setShowPlayerDataModal(true)
  }

  useEffect(() => {
    fetchRequests({ page: 1 })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])


  useEffect(() => {
    if (socket) {
      socket.on('update-requests', (data: any) => {
        if (data?.OwnerId === adminInfo?.id) {
          if (data?.request?.id === selectedRequest?.id) {
            Swal.fire({
              icon: 'info',
              title: 'Solicitud actualizada',
              text: 'La solicitud ya ha sido actualizada',
            })
            setSelectedRequest(null)
            setShowOpenRequestModal(false)
          }
          fetchRequests({ page: currentPage })
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [socket, adminInfo, selectedRequest]);

  const getRequestIsClosed = (request: Request) => {
    return request.status !== 'pending'
  }

  useEffect(() => {
    if (!showOpenPaymentRequestModal && !showOpenSubtractRequestModal && !showOpenRequestModal) {
      setSelectedRequest(null)
      setSelectedPaymentRequest(null)
      setSelectedSubtractRequest(null)
    }
  }, [showOpenPaymentRequestModal, showOpenSubtractRequestModal, showOpenRequestModal])

  return (
    <div className='pb-5 mt-4'>
      {showPlayerDataRequest && <ShowPlayerDataModal show={showPlayerDataModal} onHide={() => setShowPlayerDataModal(false)} request={showPlayerDataRequest} />}
      {selectedRequest && <OpenRequestModal show={showOpenRequestModal} onHide={() => setShowOpenRequestModal(false)} request={selectedRequest} />}
      {selectedSubtractRequest && <OpenSubtractRequest show={showOpenSubtractRequestModal} onHide={() => setShowOpenSubtractRequestModal(false)} request={selectedSubtractRequest} />}
      {selectedPaymentRequest && <OpenPaymentRequest show={showOpenPaymentRequestModal} onHide={() => setShowOpenPaymentRequestModal(false)} request={selectedPaymentRequest} />}

      <Title
        title="Solicitudes"
        icon={<PiHandCoinsFill size={30} />}
        subtitle="Solicitudes de carga y retiro de dinero de jugadores"
      />
      <div>
        {loading &&
          <div className='d-flex justify-content-center align-items-center flex-column text-primaryy' style={{ height: '20vh' }}>
            <div className="mb-1">
              Cargando página {currentPage}
            </div>
            <Spinner animation="border" variant="primary" />
          </div>
        }
      </div>
      <Row md={12} className='justify-content-end align-items-end gap-2 my-3'>
        <Col md={3}>
          <label className='text-sm text-primary'>Jugador</label>
          <input type="text" className='form-control' onChange={(e) => setFilters({ ...filters, platformUser: e.target.value })} placeholder='Busca por jugador' />
        </Col>
        <Col md={3}>
          <label className='text-sm text-primary'>Estado</label>
          <select className='form-select' value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })}>
            <option value=''>Todos</option>
            <option value='pending'>Pendiente</option>
            <option value='approved'>Aprobado</option>
            <option value='rejected'>Rechazado</option>
            <option value='pendingpayment'>Pendiente De Pago</option>
          </select>
        </Col>

        <Col md={1} className='d-flex justify-content-end'>
          <Button variant='secondary w-100' onClick={() => fetchRequests({ page: 1 })}>Buscar</Button>
        </Col>

      </Row>

      {!loading && <div>
        <Table striped bordered hover responsive>
          <thead>
            <tr>

              <th className='text-sm text-primary text-center'>Fecha</th>
              <th className='text-sm text-primary text-center'>ID</th>
              <th className='text-sm text-primary text-center'>Jugador</th>
              <th className='text-sm text-primary text-center'>Tipo</th>
              <th className='text-sm text-primary text-center'>Cantidad</th>
              <th className='text-sm text-primary text-center'>Código Promoción</th>
              {/* <th className='text-sm text-primary text-center'>Remitente</th>
                <th className='text-sm text-primary text-center'>Destinatario</th> */}
              <th className='text-sm text-primary text-center'>Estado</th>
              <th className='text-sm text-primary text-center'></th>

            </tr>
          </thead>
          <tbody>
            {requests?.map((row) => (
              <tr key={row.id}>
                <td className='text-sm text-secondary text-center'>{humanizeDateAndSetArgTimezone(row.createdAt)}</td>
                <td className='text-sm text-primary text-center'>{row.requestCode ?? '-'}</td>
                <td className='text-sm text-primary text-center'>
                  <div className=" d-flex align-items-center gap-2 justify-content-center" role="button" onClick={() => handleShowPlayerData(row)}>
                    <FaEye size={15} />
                    {row?.Player?.platformUser}
                  </div>
                </td>
                <td className={`text-sm text-center ${getTypeColor(row.type)}`}>
                  {row.firstLoadRequest ? <span className='text-success'>Primera Carga</span> : getTypeLabel(row.type)}
                </td>
                <td className='text-sm text-primary text-center'>{row.amount}</td>
                <td className='text-sm text-primary text-center'>{row.promoCode ?? '-'}</td>
                {/* <td className='text-sm text-primary text-center'>{row.sender ?? '-'}</td>
                  <td className='text-sm text-primary text-center'>{row.recipient ?? '-'}</td> */}
                <td className={`text-sm text-center ${getStatusColor(row.status)}`}>{getStatusLabel(row.status)}</td>
                <td
                  className='text-sm text-primary text-center'
                  onClick={() => handleOpenRequest(row)}>
                  <Button variant={getRequestIsClosed(row) ? 'secondary' : 'primary'}><MdOpenInNew size={20} /></Button>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
        <div className='d-flex justify-content-center'>
          <Pagination>
            <Pagination.First onClick={() => handlePageChange(1)} disabled={currentPage === 1} />
            <Pagination.Prev onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1} />
            {currentPage > 3 && (
              <>
                <Pagination.Item onClick={() => handlePageChange(1)}>1</Pagination.Item>
                <Pagination.Ellipsis disabled />
              </>
            )}
            {[...Array(totalPages)].slice(
              Math.max(0, currentPage - 3),
              Math.min(totalPages, currentPage + 2)
            ).map((_, index) => {
              const pageNumber = Math.max(1, currentPage - 2) + index;
              return (
                <Pagination.Item
                  key={pageNumber}
                  active={currentPage === pageNumber}
                  onClick={() => handlePageChange(pageNumber)}
                >
                  {pageNumber}
                </Pagination.Item>
              );
            })}
            {currentPage < totalPages - 2 && (
              <>
                <Pagination.Ellipsis disabled />
                <Pagination.Item onClick={() => handlePageChange(totalPages)}>{totalPages}</Pagination.Item>
              </>
            )}
            <Pagination.Next onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages} />
            <Pagination.Last onClick={() => handlePageChange(totalPages)} disabled={currentPage === totalPages} />
          </Pagination>
        </div>
      </div>}
    </div>
  )
}
